import { useConfirmDialog } from "@fluidattacks/design";
import { Fragment, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { authContext } from "context/auth";
import { AddUserModal } from "features/add-user-modal";
import type { IStakeholderFormValues } from "features/add-user-modal/types";
import type { IStakeholderDataSet } from "pages/organization/members/types";

interface IAddMembersModalProps {
  organizationId?: string;
  groupName?: string;
  action?: "add" | "edit";
  allMembers: IStakeholderDataSet[];
  isOpen: boolean;
  onSubmit: (values: IStakeholderFormValues) => Promise<void>;
  selectedMembers: IStakeholderDataSet[];
  close: () => void;
}

const AddMembersModal = (props: IAddMembersModalProps): React.ReactNode => {
  const {
    organizationId,
    groupName,
    allMembers,
    selectedMembers,
    action,
    onSubmit,
    isOpen,
    close,
  } = props;
  const { t } = useTranslation();
  const { userEmail } = useContext(authContext);
  const { ConfirmDialog } = useConfirmDialog();

  const domainSuggestions = useMemo((): string[] => {
    const domains = Array.from(
      new Set(
        allMembers.map(({ email }): string => {
          const [, emailDomain] = email.split("@");

          return emailDomain;
        }),
      ),
    );

    if (userEmail.endsWith("@fluidattacks.com")) {
      return domains;
    }

    return domains.filter((domain): boolean => domain !== "fluidattacks.com");
  }, [allMembers, userEmail]);

  return (
    action !== undefined && (
      <Fragment>
        <AddUserModal
          action={action}
          domainSuggestions={domainSuggestions}
          editTitle={
            groupName === undefined
              ? t("organization.tabs.users.modalEditTitle")
              : t("searchFindings.tabUsers.editStakeholderTitle")
          }
          groupName={groupName}
          initialValues={action === "edit" ? selectedMembers[0] : undefined}
          onClose={close}
          onSubmit={onSubmit}
          open={isOpen}
          organizationId={organizationId}
          suggestions={[]}
          title={
            groupName === undefined
              ? t("organization.tabs.users.modalAddTitle")
              : t("searchFindings.tabUsers.title")
          }
          type={groupName === undefined ? "organization" : "group"}
        />
        <ConfirmDialog />
      </Fragment>
    )
  );
};

export { AddMembersModal };
